// src/components/TimeTracking.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import de from 'date-fns/locale/de'; // Deutsche Lokalisierung
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import Modal from 'react-modal';
import { API_URL } from './apiurl';
import { FaBriefcase, FaHome, FaCalendarAlt, FaUser, FaClock, FaMoneyBillWave } from 'react-icons/fa';

// Registrierung der deutschen Lokalisierung
registerLocale('de', de);

// React-Modal (Accessibility)
Modal.setAppElement('#root');

/** 
* Funktion zur Konvertierung eines ISO-Datumsstrings zu 'YYYY-MM-DD'
* @param {string} isoDateStr - Datum im ISO-Format (z.B. '2024-12-31T23:00:00.000Z')
* @returns {string} - Datum im Format 'YYYY-MM-DD'
*/
function formatDateToSQL(isoDateStr) {
const date = new Date(isoDateStr);
if (isNaN(date.getTime())) {
  throw new Error('Ungültiges Datumsformat.');
}
const yyyy = date.getFullYear();
const mm = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind 0-indexiert
const dd = String(date.getDate()).padStart(2, '0');
return `${yyyy}-${mm}-${dd}`;
}

// Custom Styles für react-select
const customSelectStyles = {
control: (provided) => ({
  ...provided,
  borderRadius: '8px',
  borderColor: '#d1d5db',
  fontSize: '0.95rem',
  boxShadow: 'none',
  '&:hover': {
    borderColor: '#3B82F6',
  },
}),
option: (provided, state) => ({
  ...provided,
  backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EFF6FF' : null,
  color: state.isSelected ? 'white' : '#374151',
}),
};

// Custom Styles für react-modal
const modalStyles = {
content: {
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  marginRight: '-50%',
  transform: 'translate(-50%, -50%)',
  width: '500px',
  maxWidth: '90%',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
  border: 'none',
},
overlay: {
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1000,
}
};

// Custom Input für DatePicker
const CustomDateInput = React.forwardRef(({ value, onClick, onChange, placeholder }, ref) => (
<div style={styles.datePickerWrapper}>
  <input
    type="text"
    onClick={onClick}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    ref={ref}
    style={styles.datePickerInput}
    readOnly
  />
  <FaCalendarAlt style={styles.datePickerIcon} onClick={onClick} />
</div>
));

export default function TimeTracking() {
const { auth, logout } = useContext(AuthContext);

// Zeit-Einträge
const [entries, setEntries] = useState([]);

// Aktiver Tab (normal, abwesenheit, spesen)
const [activeTab, setActiveTab] = useState('normal');

// Tab-Animation State
const [isTabChanging, setIsTabChanging] = useState(false);

// Neue Eintrag-Formularfelder
const [date, setDate] = useState(new Date());
const [type, setType] = useState('normal');
const [subject, setSubject] = useState('');
const [address, setAddress] = useState('');
const [hours, setHours] = useState('8.00');
const [startTime, setStartTime] = useState('09:00');
const [endTime, setEndTime] = useState('17:00');

// Kunden
const [customers, setCustomers] = useState([]);
const [selectedCustomer, setSelectedCustomer] = useState(null);

// Vorgaben aus Settings
const [hoursForKrank, setHoursForKrank] = useState('0');
const [hoursForUrlaub, setHoursForUrlaub] = useState('0');
const [hoursForFeiertag, setHoursForFeiertag] = useState('0');

// Stundensatz pro Jahr
const [stundensatzJahr, setStundensatzJahr] = useState('0');
const [stundensatzNormal, setStundensatzNormal] = useState('0');
const [stundensatzJahrFachgebiet, setStundensatzJahrFachgebiet] = useState('0');

// Checkboxen
const [hotelspeesen, setHotelspeesen] = useState(false);
const [verpflegungsMehraufwand, setVerpflegungsMehraufwand] = useState(false);
const [finanzbuchhaltung, setFinanzbuchhaltung] = useState(false);
const [workflow, setWorkflow] = useState(false);
const [homeoffice, setHomeoffice] = useState(false);

// Modal-Status für neuen Kunden
const [isModalOpen, setIsModalOpen] = useState(false);
const [newCustomer, setNewCustomer] = useState({
  vorname: '',
  nachname: '',
  strasse: '',
  plz: '',
  ort: '',
  telefon: '',
  email: '',
});

// Form Feedback Status
const [submitStatus, setSubmitStatus] = useState(null);

// Effekt: Daten laden
useEffect(() => {
  const fetchEntries = async () => {
    try {
      const response = await fetch(`${API_URL}/entries`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Laden der Zeiteinträge.');
      }

      const data = await response.json();
      setEntries(data);
    } catch (err) {
      console.error('Fehler beim Laden von Zeiteinträgen:', err);
      toast.error(err.message || 'Fehler beim Laden der Zeiteinträge.');
      if (err.message === 'Token-Authentifizierung fehlgeschlagen') {
        logout();
      }
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/customers`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Laden der Kunden.');
      }

      const data = await response.json();
      setCustomers(data);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
      toast.error(err.message || 'Fehler beim Laden der Kunden.');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Laden der Einstellungen.');
      }

      const data = await response.json();
      // Stunden für Krank/Urlaub/Feiertag
      setHoursForKrank(data.hoursForKrank ? data.hoursForKrank.toFixed(2) : '0.00');
      setHoursForUrlaub(data.hoursForUrlaub ? data.hoursForUrlaub.toFixed(2) : '0.00');
      setHoursForFeiertag(data.hoursForFeiertag ? data.hoursForFeiertag.toFixed(2) : '0.00');

      // Stundensatz pro Jahr
      const stundensatzJahre =
        typeof data.hourlyWage === 'string'
          ? JSON.parse(data.hourlyWage)
          : data.hourlyWage;

      const aktuellesJahr = new Date().getFullYear();
      if (stundensatzJahre[aktuellesJahr]) {
        setStundensatzJahr(stundensatzJahre[aktuellesJahr].stundensatz);
        setStundensatzJahrFachgebiet(stundensatzJahre[aktuellesJahr].fachgebiet);
        setStundensatzNormal(stundensatzJahre[aktuellesJahr].stundensatz);
      }
    } catch (err) {
      console.error('Fehler beim Laden von Einstellungen:', err);
      toast.error(err.message || 'Fehler beim Laden der Einstellungen.');
    }
  };

  fetchEntries();
  fetchCustomers();
  fetchSettings();
  // eslint-disable-next-line
}, [auth.token]);

// Effekt: Tab wechseln basierend auf dem Typ
useEffect(() => {
  if (type === 'normal' || type === 'halb-vormittag' || type === 'halb-nachmittag') {
    setActiveTab('normal');
  } else if (type === 'krank' || type === 'urlaub' || type === 'feiertag') {
    setActiveTab('abwesenheit');
    
    // Bei Abwesenheit Spesen und Homeoffice deaktivieren
    setHotelspeesen(false);
    setVerpflegungsMehraufwand(false);
    setHomeoffice(false);
  }
}, [type]);

// Effekt: Wenn sich der Typ ändert, passe (ggf.) die Stunden an
useEffect(() => {
  if (type === 'krank') {
    setHours(hoursForKrank);
  } else if (type === 'urlaub') {
    setHours(hoursForUrlaub);
  } else if (type === 'feiertag') {
    setHours(hoursForFeiertag);
  } else if (type === 'halb-vormittag' || type === 'halb-nachmittag') {
    // Beispiel: Standard 8h als Tageswert
    const halfDay = 8 / 2;
    setHours(halfDay.toFixed(2));
    
    // Passende Zeiten setzen
    if (type === 'halb-vormittag') {
      setStartTime('09:00');
      setEndTime('13:00');
    } else {
      setStartTime('13:00');
      setEndTime('17:00');
    }
  }
}, [type, hoursForKrank, hoursForUrlaub, hoursForFeiertag]);

// Hilfsfunktion: Stundendauer aus Start- und Endzeit berechnen
function calculateTotalHours(start, end) {
  if (!start || !end) return '0.00';
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  const total = (endH + endM / 60) - (startH + startM / 60);
  if (total < 0) return '0.00'; // Fallback
  return total.toFixed(2);
}

// Submit-Hanlder: Neuen Eintrag speichern
const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitStatus('submitting');
  
  // Datum validieren
  if (!date || !(date instanceof Date) || isNaN(date)) {
    toast.error('Bitte wählen Sie ein gültiges Datum aus.');
    setSubmitStatus('error');
    return;
  }
  
  const dateStr = formatDateToSQL(date);

  const newEntry = {
    date: dateStr,
    type,
    subject,
    address,
    startTime: type === 'normal' || type === 'halb-vormittag' || type === 'halb-nachmittag' ? startTime : '',
    endTime: type === 'normal' || type === 'halb-vormittag' || type === 'halb-nachmittag' ? endTime : '',
    hours: parseFloat(hours),
    hotelspeesen,
    verpflegungsMehraufwand,
    customerId: selectedCustomer ? selectedCustomer.value : null,
    hourlyWage: parseFloat(stundensatzJahr),
    fachgebiet: workflow ? 1 : finanzbuchhaltung ? 2 : 0,
    homeoffice: homeoffice
  };

  try {
    const response = await fetch(`${API_URL}/entries`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify(newEntry),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Fehler beim Speichern des Eintrags.');
    }

    // Erfolg
    setEntries([...entries, data]);
    toast.success('Eintrag gespeichert!');
    setSubmitStatus('success');

    // Formular zurücksetzen
    setSubject('');
    setAddress('');
    setType('normal');
    setStartTime('09:00');
    setEndTime('17:00');
    setHours('8.00');
    setHotelspeesen(false);
    setVerpflegungsMehraufwand(false);
    setDate(new Date());
    setSelectedCustomer(null);
    setWorkflow(false);
    setFinanzbuchhaltung(false);
    setHomeoffice(false);
    setActiveTab('normal');

    // Reset status after short delay
    setTimeout(() => setSubmitStatus(null), 2000);

  } catch (err) {
    console.error('Fehler beim Speichern des Eintrags:', err);
    toast.error(err.message || 'Serverfehler beim Speichern des Eintrags.');
    setSubmitStatus('error');
    
    // Reset error status after delay
    setTimeout(() => setSubmitStatus(null), 2000);
  }
};

// Kunden-Optionen für react-select
const customerOptions = customers.map((c) => ({
  value: c.id,
  label: `${c.vorname || ''} ${c.nachname}`,
}));

// Modal öffnen/schließen
const openModal = () => setIsModalOpen(true);
const closeModal = () => {
  setIsModalOpen(false);
  setNewCustomer({
    vorname: '',
    nachname: '',
    strasse: '',
    plz: '',
    ort: '',
    telefon: '',
    email: '',
  });
};

// Neuen Kunden erstellen
const handleCreateCustomer = async (e) => {
  e.preventDefault();
  const { nachname } = newCustomer;

  if (!nachname.trim()) {
    toast.error('Nachname ist erforderlich.');
    return;
  }

  try {
    const response = await fetch(`${API_URL}/customers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      body: JSON.stringify(newCustomer),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Fehler beim Erstellen des Kunden.');
    }

    toast.success('Kunde erfolgreich erstellt!');
    // Lokalen State updaten
    setCustomers([...customers, data]);
    setSelectedCustomer({ value: data.id, label: `${data.vorname || ''} ${data.nachname}` });
    closeModal();
  } catch (err) {
    console.error('Fehler beim Erstellen des Kunden:', err);
    toast.error(err.message || 'Serverfehler beim Erstellen des Kunden.');
  }
};

// Handler für geänderte Eingaben (Text, Time, etc.)
const handleInputChange = (setter) => (e) => {
  setter(e.target.value);
  
  // Für Textarea auto-resize wenn nötig
  if (e.target.tagName.toLowerCase() === 'textarea') {
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(200, Math.max(60, e.target.scrollHeight))}px`;
  }
};

// Handler für Checkboxen
const handleCheckboxChange = (setter) => (e) => {
  setter(e.target.checked);
};

// Handler für Fachgebiet-Checkboxen (mit Stundensatz-Anpassung)
const handleFachgebietChange = (setter, otherSetter) => (e) => {
  const isChecked = e.target.checked;
  
  // Wenn aktiviert wird, deaktiviere die andere Checkbox
  if (isChecked) {
    otherSetter(false);
    // Stundensatz auf Fachgebiet anpassen
    setStundensatzJahr(stundensatzJahrFachgebiet);
  } else {
    // Zurück zum normalen Stundensatz, wenn keine Fachgebiet aktiv
    if (!workflow && !finanzbuchhaltung) {
      setStundensatzJahr(stundensatzNormal);
    }
  }
  
  setter(isChecked);
};

// Handler für Start-/Endzeit Änderungen
const handleTimeChange = (newStartTime, newEndTime) => {
  setHours(calculateTotalHours(newStartTime, newEndTime));
};

// Tab-Wechsel Handler - optimiert für flüssige Übergänge
const handleTabChange = (tab) => {
  // Keine Aktion, wenn der Tab bereits aktiv ist
  if (tab === activeTab) return;
  
  // Animation für den Übergang
  setIsTabChanging(true);
  
  // Kurze Verzögerung für den visuellen Effekt
  setTimeout(() => {
    setActiveTab(tab);
    
    // State-Updates basierend auf Tab
    switch(tab) {
      case 'normal':
        setType('normal');
        break;
      case 'abwesenheit':
        setType('urlaub');
        setHours(hoursForUrlaub);
        setHotelspeesen(false);
        setVerpflegungsMehraufwand(false);
        setHomeoffice(false);
        break;
      default:
        break;
    }
    
    // Animation beenden
    setTimeout(() => {
      setIsTabChanging(false);
    }, 50);
  }, 100);
};

// Render verschiedene Tabs basierend auf aktivem Tab
const renderTabContent = () => {
  switch(activeTab) {
    case 'normal':
      return (
        <>
          {/* Typ des Arbeitstags */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Art des Arbeitstags:</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={styles.input}
              >
                <option value="normal">Ganzer Tag</option>
                <option value="halb-vormittag">Halber Tag (Vormittag)</option>
                <option value="halb-nachmittag">Halber Tag (Nachmittag)</option>
              </select>
            </div>
          </div>

          {/* Zeit- und Stunden-Eingaben */}
          <div style={styles.cardSection}>
            <h3 style={styles.sectionTitle}>
              <FaClock style={styles.sectionIcon} /> Zeiterfassung
            </h3>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Von:</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setStartTime(newStart);
                    handleTimeChange(newStart, endTime);
                  }}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Bis:</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => {
                    const newEnd = e.target.value;
                    setEndTime(newEnd);
                    handleTimeChange(startTime, newEnd);
                  }}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Stunden:</label>
                <input
                  type="number"
                  step="0.01"
                  value={hours}
                  onChange={handleInputChange(setHours)}
                  style={{...styles.input, fontWeight: 'bold'}}
                  required
                />
              </div>
            </div>
          </div>

          {/* Fachgebiete */}
          <div style={styles.cardSection}>
            <h3 style={styles.sectionTitle}>
              <FaBriefcase style={styles.sectionIcon} /> Fachgebiet & Arbeitsort
            </h3>
            <div style={styles.formRow}>
              <div style={{...styles.formGroup, flex: 1}}>
                <div style={styles.checkboxContainer}>
                  <div style={styles.checkGroup}>
                    <input
                      type="checkbox"
                      id="workflow-check"
                      checked={workflow}
                      onChange={handleFachgebietChange(setWorkflow, setFinanzbuchhaltung)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="workflow-check" style={styles.checkLabel}>
                      Workflow
                    </label>
                  </div>
                  
                  <div style={styles.checkGroup}>
                    <input
                      type="checkbox"
                      id="finanzbuchhaltung-check"
                      checked={finanzbuchhaltung}
                      onChange={handleFachgebietChange(setFinanzbuchhaltung, setWorkflow)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="finanzbuchhaltung-check" style={styles.checkLabel}>
                      Finanzbuchhaltung
                    </label>
                  </div>
                </div>
              </div>
              
              <div style={{...styles.formGroup, flex: 1}}>
                <div style={styles.checkGroup}>
                  <input
                    type="checkbox"
                    id="homeoffice-check"
                    checked={homeoffice}
                    onChange={handleCheckboxChange(setHomeoffice)}
                    style={styles.checkbox}
                  />
                  <label htmlFor="homeoffice-check" style={{...styles.checkLabel, display: 'flex', alignItems: 'center'}}>
                    <FaHome style={{marginRight: '0.5rem'}} /> Homeoffice
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Stundensatz und Spesen */}
          <div style={styles.cardSection}>
            <h3 style={styles.sectionTitle}>
              <FaMoneyBillWave style={styles.sectionIcon} /> Abrechnung & Spesen
            </h3>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Stundensatz (€):</label>
                <input
                  type="number"
                  step="0.01"
                  value={stundensatzJahr}
                  onChange={handleInputChange(setStundensatzJahr)}
                  style={{...styles.input, fontWeight: 'bold'}}
                  required
                />
              </div>
            </div>
            
            {/* Spesenoptionen - deutlicher hervorgehoben */}
            <div style={styles.spesenContainer}>
              <h4 style={styles.spesenTitle}>Spesenerfassung:</h4>
              <div style={styles.formRow}>
                <div style={{...styles.formGroup, justifyContent: 'center'}}>
                  <div style={styles.checkGroup}>
                    <input
                      type="checkbox"
                      id="hotelspeesen-check"
                      checked={hotelspeesen}
                      onChange={handleCheckboxChange(setHotelspeesen)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="hotelspeesen-check" style={styles.checkLabel}>
                      Hotelspeesen
                    </label>
                  </div>
                </div>
                
                <div style={{...styles.formGroup, justifyContent: 'center'}}>
                  <div style={styles.checkGroup}>
                    <input
                      type="checkbox"
                      id="verpflegung-check"
                      checked={verpflegungsMehraufwand}
                      onChange={handleCheckboxChange(setVerpflegungsMehraufwand)}
                      style={styles.checkbox}
                    />
                    <label htmlFor="verpflegung-check" style={styles.checkLabel}>
                      Verpflegungsmehraufwand
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    
    case 'abwesenheit':
      return (
        <>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Art der Abwesenheit:</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={styles.input}
              >
                <option value="urlaub">Urlaub</option>
                <option value="krank">Krankheit</option>
                <option value="feiertag">Feiertag</option>
              </select>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Stunden:</label>
              <input
                type="number"
                step="0.01"
                value={hours}
                onChange={handleInputChange(setHours)}
                style={{...styles.input, fontWeight: 'bold'}}
                required
              />
            </div>
          </div>
          
          {/* Hinweis zu deaktivierten Optionen */}
          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              Bei Abwesenheitstypen sind Optionen wie Homeoffice, Hotelspeesen und 
              Verpflegungsmehraufwand nicht verfügbar.
            </p>
          </div>
        </>
      );
      
    default:
      return null;
  }
};

return (
  <div style={styles.container}>
    <h2 style={styles.title}>Zeiterfassung</h2>

    <div style={styles.card}>
      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basis-Informationen - immer sichtbar */}
        <div style={styles.cardSection}>
          <h3 style={styles.sectionTitle}>
            <FaCalendarAlt style={styles.sectionIcon} /> Basisdaten
          </h3>
          
          {/* Erste Zeile: Datum und Kunde */}
          <div style={styles.formRow}>
            {/* Datum */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Datum:</label>
              <DatePicker
                selected={date}
                onChange={(val) => setDate(val)}
                dateFormat="dd.MM.yyyy"
                locale="de"
                placeholderText="TT.MM.JJJJ"
                maxDate={new Date(2100, 11, 31)}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                customInput={<CustomDateInput />}
              />
            </div>

            {/* Kunden-Auswahl */}
            <div style={{...styles.formGroup, flex: 2}}>
              <label style={styles.label}>
                <FaUser style={{marginRight: '0.4rem'}} /> Kunde:
              </label>
              <div style={styles.customerSelectContainer}>
                <Select
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                  options={customerOptions}
                  isClearable
                  placeholder="Kunde auswählen"
                  styles={customSelectStyles}
                  components={makeAnimated()}
                  className="customer-select"
                />
                <button 
                  type="button" 
                  onClick={openModal} 
                  style={styles.addButton}
                  title="Neuen Kunden anlegen"
                >
                  + Neu
                </button>
              </div>
            </div>
          </div>

          {/* Betreff und Adresse */}
          <div style={styles.formRow}>
            <div style={{...styles.formGroup, flex: 2}}>
              <label style={styles.label}>Betreff / Auftrag:</label>
              <textarea
                value={subject}
                onChange={handleInputChange(setSubject)}
                style={{...styles.input, minHeight: '60px', resize: 'vertical'}}
                placeholder="z.B. Projekt XY oder Beschreibung der Tätigkeit"
                rows={Math.min(4, Math.max(2, (subject.split('\n').length || 1)))}
              />
            </div>
            
            <div style={{...styles.formGroup, flex: 1}}>
              <label style={styles.label}>Adresse:</label>
              <input
                type="text"
                value={address}
                onChange={handleInputChange(setAddress)}
                style={styles.input}
                placeholder="z.B. Musterstraße 1"
              />
            </div>
          </div>
        </div>

        {/* Tabs für verschiedene Eintragstypen */}
        <div style={styles.tabContainer}>
          <button
            type="button"
            onClick={() => handleTabChange('normal')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'normal' ? styles.activeTab : {})
            }}
          >
            <FaBriefcase style={styles.tabIcon} />
            Arbeitstag
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('abwesenheit')}
            style={{
              ...styles.tabButton,
              ...(activeTab === 'abwesenheit' ? styles.activeTab : {})
            }}
          >
            <FaCalendarAlt style={styles.tabIcon} />
            Abwesenheit
          </button>
        </div>

        {/* Tab-Inhalt rendern */}
        <div 
          style={{
            ...styles.tabContent,
            opacity: isTabChanging ? 0 : 1
          }}
        >
          {renderTabContent()}
        </div>

        {/* Speichern-Button */}
        <button 
          type="submit" 
          style={{
            ...styles.saveEntryButton,
            ...(submitStatus === 'submitting' ? styles.buttonSubmitting : {}),
            ...(submitStatus === 'success' ? styles.buttonSuccess : {}),
            ...(submitStatus === 'error' ? styles.buttonError : {})
          }}
          disabled={submitStatus === 'submitting'}
        >
          {submitStatus === 'submitting' ? 'Wird gespeichert...' : 
           submitStatus === 'success' ? 'Gespeichert ✓' : 
           submitStatus === 'error' ? 'Fehler!' : 'Eintrag speichern'}
        </button>
      </form>
    </div>

    {/* Modal: Neuen Kunden erstellen */}
    <Modal
      isOpen={isModalOpen}
      onRequestClose={closeModal}
      contentLabel="Neuen Kunden erstellen"
      style={modalStyles}
    >
      <h2 style={styles.modalTitle}>Neuen Kunden erstellen</h2>
      <form onSubmit={handleCreateCustomer} style={styles.form}>
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Vorname:</label>
            <input
              type="text"
              value={newCustomer.vorname}
              onChange={(e) => setNewCustomer({ ...newCustomer, vorname: e.target.value })}
              style={styles.input}
              placeholder="Vorname"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Nachname*:</label>
            <input
              type="text"
              value={newCustomer.nachname}
              onChange={(e) => setNewCustomer({ ...newCustomer, nachname: e.target.value })}
              style={styles.input}
              placeholder="Nachname"
              required
            />
          </div>
        </div>
        
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Straße:</label>
            <input
              type="text"
              value={newCustomer.strasse}
              onChange={(e) => setNewCustomer({ ...newCustomer, strasse: e.target.value })}
              style={styles.input}
              placeholder="Straße"
            />
          </div>
        </div>
        
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>PLZ:</label>
            <input
              type="text"
              value={newCustomer.plz}
              onChange={(e) => setNewCustomer({ ...newCustomer, plz: e.target.value })}
              style={styles.input}
              placeholder="PLZ"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Ort:</label>
            <input
              type="text"
              value={newCustomer.ort}
              onChange={(e) => setNewCustomer({ ...newCustomer, ort: e.target.value })}
              style={styles.input}
              placeholder="Ort"
            />
          </div>
        </div>
        
        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Telefon:</label>
            <input
              type="text"
              value={newCustomer.telefon}
              onChange={(e) => setNewCustomer({ ...newCustomer, telefon: e.target.value })}
              style={styles.input}
              placeholder="Telefon"
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email:</label>
            <input
              type="email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              style={styles.input}
              placeholder="Email"
            />
          </div>
        </div>
        
        <div style={styles.buttonGroup}>
          <button type="submit" style={styles.saveButton}>
            Erstellen
          </button>
          <button type="button" onClick={closeModal} style={styles.cancelButton}>
            Abbrechen
          </button>
        </div>
      </form>
    </Modal>
  </div>
);
}

/** Verbesserte Inline-Styles mit visueller Hierarchie und besserer Organisation */
const styles = {
container: {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '1.5rem',
  fontFamily: 'Roboto, sans-serif',
  backgroundColor: '#f9fafb',
  borderRadius: '12px',
},
infoBox: {
  backgroundColor: '#EFF6FF',
  borderLeft: '4px solid #3B82F6',
  padding: '0.8rem 1rem',
  marginTop: '0.5rem',
  borderRadius: '0 4px 4px 0',
},
infoText: {
  margin: 0,
  color: '#4B5563',
  fontSize: '0.9rem',
},
title: {
  textAlign: 'center',
  marginBottom: '1.5rem',
  fontSize: '1.8rem',
  color: '#374151',
  fontWeight: '600',
},
card: {
  backgroundColor: '#fff',
  borderRadius: '12px',
  boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
  padding: '1.75rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
},
form: {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem',
},
cardSection: {
  borderBottom: '1px solid #f0f0f0',
  paddingBottom: '1.25rem',
  marginBottom: '0.5rem',
},
sectionTitle: {
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#4B5563',
  marginBottom: '1rem',
  display: 'flex',
  alignItems: 'center',
},
sectionIcon: {
  marginRight: '0.5rem',
  color: '#3B82F6',
},
formRow: {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
},
formGroup: {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  flex: 1,
  minWidth: '200px',
},
label: {
  fontWeight: '500',
  color: '#374151',
  fontSize: '0.95rem',
  marginBottom: '0.25rem',
  display: 'flex',
  alignItems: 'center',
},
input: {
  padding: '0.65rem',
  fontSize: '1rem',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
  transition: 'all 0.2s',
  width: '100%',
  boxSizing: 'border-box',
  '&:focus': {
    borderColor: '#3B82F6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)',
  },
},
datePickerWrapper: {
  position: 'relative',
  width: '100%',
},
datePickerInput: {
  padding: '0.65rem',
  fontSize: '1rem',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
  transition: 'all 0.2s',
  width: '100%',
  boxSizing: 'border-box',
  cursor: 'pointer',
},
datePickerIcon: {
  position: 'absolute',
  right: '10px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#6B7280',
  cursor: 'pointer',
},
customerSelectContainer: {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
  width: '100%',
},
checkboxContainer: {
  display: 'flex',
  flexDirection: 'row',
  gap: '1.5rem',
  marginTop: '0.25rem',
},
checkGroup: {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
},
checkLabel: {
  fontSize: '0.95rem',
  fontWeight: '500',
  color: '#4B5563',
},
checkbox: {
  width: '16px',
  height: '16px',
  cursor: 'pointer',
},
tabContainer: {
  display: 'flex',
  borderBottom: '1px solid #e5e7eb',
  marginBottom: '1.5rem',
},
tabButton: {
  padding: '0.75rem 1.25rem',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: '3px solid transparent',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '0.95rem',
  color: '#6B7280',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.25s ease-in-out',
},
activeTab: {
  borderBottom: '3px solid #3B82F6',
  color: '#3B82F6',
  fontWeight: '600',
},
tabIcon: {
  marginRight: '0.5rem',
},
tabContent: {
  padding: '0.5rem 0',
  opacity: 1,
  transition: 'opacity 0.2s ease-in-out',
  animation: 'fadeIn 0.2s ease-in-out',
},
saveEntryButton: {
  backgroundColor: '#3B82F6',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 'bold',
  padding: '0.85rem',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  marginTop: '1rem',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: '#2563EB',
  },
},
buttonSubmitting: {
  backgroundColor: '#6B7280',
  cursor: 'not-allowed',
},
buttonSuccess: {
  backgroundColor: '#10B981',
},
buttonError: {
  backgroundColor: '#EF4444',
},
addButton: {
  backgroundColor: '#10b981',
  color: '#fff',
  fontSize: '0.9rem',
  border: 'none',
  borderRadius: '8px',
  padding: '0.5rem 1rem',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  minWidth: '80px',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: '#059669',
  },
},
modalTitle: {
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '1rem',
  marginBottom: '1.5rem',
  color: '#374151',
},
buttonGroup: {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  marginTop: '1.5rem',
},
saveButton: {
  backgroundColor: '#10b981',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '0.65rem 1.25rem',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '500',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: '#059669',
  },
},
cancelButton: {
  backgroundColor: '#6b7280',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  padding: '0.65rem 1.25rem',
  cursor: 'pointer',
  fontSize: '1rem',
  fontWeight: '500',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: '#4B5563',
  },
},
spesenContainer: {
  backgroundColor: '#f8f9fa',
  padding: '0.8rem 1rem',
  borderRadius: '8px',
  marginTop: '1rem',
  border: '1px solid #e5e7eb',
},
spesenTitle: {
  fontSize: '0.95rem',
  fontWeight: '600',
  color: '#4B5563',
  marginBottom: '0.75rem',
},
};